import { StyledCard } from '@/components/Card/StyledCard';
import { Heading } from '@/components/Text/Heading/Heading';
import {
  type PlanningContextType,
  StateContext,
} from '@/layouts/Planning/PlanningLayout';
import {
  type ClientPlan,
  type ClientPlanClient,
  type ClientPlans,
} from '@/schemas/API/GET/client-plan/response';
import { Button, Input, Stack, Text } from '@mantine/core';
import { useContext, useState, type ReactElement, useEffect } from 'react';
import { getFetcher } from '@/api/fetchers';

interface ClientSearchCardProps {
  planState: string;
}

const toFullName = (client: ClientPlanClient): string => {
  return client.lastName + ' ' + client.firstName;
};

const getList = (
  clientPlans: ClientPlan[],
  filter: string,
  selected: number | undefined
): ClientPlan[] => {
  const selectedPlan = clientPlans.find(plan => plan.carePlanId === selected);
  const remaining = clientPlans.filter(
    plan =>
      toFullName(plan.client).toLowerCase().includes(filter.toLowerCase()) &&
      plan.carePlanId !== selected
  );

  if (selectedPlan) return [selectedPlan, ...remaining];
  return remaining;
};

export const ClientSearchCard = ({
  planState,
}: ClientSearchCardProps): ReactElement => {
  const { setClientPlanId, clientPlanId, planId, service, setClientName } =
    useContext<PlanningContextType>(StateContext);
  const [input, setInput] = useState<string>('');
  const [clientPlans, setClientPlans] = useState<ClientPlans | undefined>(
    undefined
  );

  useEffect(() => {
    setClientPlans(undefined);
  }, [planId, service, planState]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let page = 1;
        let last = false;

        while (!last) {
          const response = await getFetcher(
            `services/${service}/plans/${planId}/client-plans?assignTasksDiff=desc&page=${page}&size=20&state=${planState}`
          );
          last = response.last;
          page++;
          setClientPlans(prevPlans => {
            if (!prevPlans) {
              return response;
            }
            return {
              ...response,
              content: [...prevPlans.content, ...response.content],
            };
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (planId) {
      fetchData();
    } else {
      setClientPlans(undefined);
      setClientName(undefined);
    }
  }, [planId, service, planState]);

  const diffToColor = (diff: number): string => {
    if (diff === 0) return 'green';
    if (diff > 10) return 'red';
    return 'yellow';
  };

  return (
    <StyledCard>
      <Stack gap="sm" className="max-h-144">
        <Heading type="tertiary">Vyhledávání klientů</Heading>
        <Input value={input} onChange={e => setInput(e.currentTarget.value)} />
        <Stack gap="md" className="max-h-96 overflow-scroll">
          {getList(clientPlans?.content ?? [], input, clientPlanId).map(
            plan => (
              <Button
                key={plan.client.id}
                className="overflow-visible"
                color="gray"
                fullWidth
                justify="space-between"
                variant={plan.carePlanId === clientPlanId ? 'light' : 'subtle'}
                rightSection={
                  <Text c={diffToColor(plan.info.assignTasksDiff)}>
                    {plan.info.assignTasksCount}/{plan.info.tasksCount}
                  </Text>
                }
                onClick={() => {
                  setClientPlanId(plan.id);
                  setClientName(
                    plan.client.firstName + ' ' + plan.client.lastName
                  );
                }}
              >
                {toFullName(plan.client)}
              </Button>
            )
          )}
        </Stack>
      </Stack>
    </StyledCard>
  );
};
